import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type Plan = "free" | "starter" | "growth";

interface SubscriptionState {
  plan: Plan;
  subscribed: boolean;
  subscriptionEnd: string | null;
  analysesUsed: number;
  analysesLimit: number | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useSubscription(): SubscriptionState {
  const { user, session } = useAuth();
  const [plan, setPlan] = useState<Plan>("free");
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [analysesUsed, setAnalysesUsed] = useState(0);
  const [analysesLimit, setAnalysesLimit] = useState<number | null>(1);
  const [loading, setLoading] = useState(true);

  const checkSubscription = useCallback(async () => {
    if (!session?.access_token) {
      setPlan("free");
      setSubscribed(false);
      setSubscriptionEnd(null);
      setAnalysesUsed(0);
      setAnalysesLimit(1);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;
      setPlan((data?.plan as Plan) || "free");
      setSubscribed(data?.subscribed || false);
      setSubscriptionEnd(data?.subscription_end || null);
      setAnalysesUsed(data?.analyses_used ?? 0);
      setAnalysesLimit(data?.analyses_limit ?? null);
    } catch (err) {
      console.error("Failed to check subscription:", err);
      setPlan("free");
      setSubscribed(false);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Auto-refresh every 60s
  useEffect(() => {
    if (!session?.access_token) return;
    const interval = setInterval(checkSubscription, 60000);
    return () => clearInterval(interval);
  }, [session?.access_token, checkSubscription]);

  return { plan, subscribed, subscriptionEnd, analysesUsed, analysesLimit, loading, refresh: checkSubscription };
}

// Feature access helpers
export function canAccess(plan: Plan, feature: "actionPlan" | "competitor" | "linkIntelligence" | "content" | "aiChat" | "backlinkOpportunities"): boolean {
  switch (feature) {
    case "actionPlan":
    case "competitor":
    case "linkIntelligence":
      return plan === "starter" || plan === "growth";
    case "content":
    case "aiChat":
    case "backlinkOpportunities":
      return plan === "growth";
    default:
      return false;
  }
}
